/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import { PageTitleWithAreaIntro, Text } from '@/shared/ui';
import { navIconFor } from '@layout/agency/agency-nav';
import {
  PartnershipsWizardProvider,
  usePartnershipsWizard,
} from '../context/PartnershipsWizardContext';
import { SourceStep } from '../components/SourceStep';
import { FiltersStep } from '../components/FiltersStep';
import { ResultsStep } from '../components/ResultsStep';

/**
 * Renders the active wizard step (source, filters, or results) beneath the
 * page title and intro, based on the current step in the wizard store.
 *
 * @returns The wizard body for the current step.
 */
function WizardContent() {
  const { t } = useTranslation('features/partnerships');
  const step = usePartnershipsWizard((s) => s.step);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex flex-col gap-(--space-1) px-(--space-6) pt-(--space-6) pb-(--space-3)">
        <PageTitleWithAreaIntro
          icon={navIconFor('partnerships')}
          intro={{
            label: t('intro.label'),
            body: t('intro.body'),
            testIdPrefix: 'partnerships',
          }}
        >
          {t('page_title')}
        </PageTitleWithAreaIntro>
        <Text variant="body" size="sm" c="dimmed">
          {t('page_subtitle')}
        </Text>
      </div>
      <div className="min-h-0 flex-1 overflow-auto px-(--space-6) pb-(--space-6)">
        <div className="flex flex-col gap-(--space-5)">
          {step === 'source' && <SourceStep />}
          {step === 'filters' && <FiltersStep />}
          {step === 'results' && <ResultsStep />}
        </div>
      </div>
    </div>
  );
}

/**
 * Partner-portal search wizard page: wraps the multi-step flow in its state
 * provider.
 *
 * @returns The partnerships wizard page.
 */
export default function PartnershipsWizardPage() {
  return (
    <PartnershipsWizardProvider>
      <WizardContent />
    </PartnershipsWizardProvider>
  );
}
